import * as React from "react"
import { ChevronLeft, TriangleAlert } from "lucide-react"

import { HELP_TEXT, META_TEXT, TAP_TARGET } from "@/lib/ui-tokens"
import { cn } from "@/lib/utils"
import { Alert, AlertDescription } from "@/shadcn/components/ui/alert"
import { Button } from "@/shadcn/components/ui/button"
import { Input } from "@/shadcn/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select"
import { Switch } from "@/shadcn/components/ui/switch"

/*
 * The settings for one destination's link page: where it is turned on, named
 * and sized. Figma 30015:2060 (off), 30015:2166 (on), 30015:2917 (on, X).
 *
 * STANDALONE ON PURPOSE. The design wears the shared settings shell
 * (settings-page.tsx + settings-form.tsx), which is still only on
 * feature/settingsRefresh. Until it reaches develop this draws the same anatomy
 * by hand, using the shell's own classes, so the move onto it is a
 * straight swap for `buildLinkPageSections()` + `<SettingsPage>`. It
 * changes nothing visible, apart from the `link` field kind, which the shell
 * has yet to gain.
 *
 * THE SWITCH GOVERNS THE SCREEN: with the page off, the other rows are
 * absent, not disabled. Their draft values survive the switch going off and
 * on again, but are not saved while it is off.
 *
 * No Redux here. The container (app/views/LinkpageSettings.jsx) owns the
 * record, the request state and the messages; this owns only the draft.
 */

export type LinkpageSettingsValues = {
  enabled: boolean
  url: string
  title: string
  website: string
  itemsShown: number
}

type Draft = Pick<LinkpageSettingsValues, "enabled" | "title" | "website" | "itemsShown">

export type LinkpageSettingsField = keyof Draft

export type LinkpageSettingsProps = {
  accountName: string
  networkLabel: string
  /** "x" for dlvrit.me/x/<name>. Omitted for Instagram, which owns the bare path. */
  pathSegment?: string
  /** The saved record, in the shape app/utility/LinkpageSettings.js produces. */
  settings?: Partial<LinkpageSettingsValues>
  loading?: boolean
  saving?: boolean
  /** Replaces the form: the record could not be loaded, or there is none to load. */
  loadError?: string | null
  onRetry?: () => void
  formError?: string | null
  fieldErrors?: Partial<Record<LinkpageSettingsField, string>>
  onBack: () => void
  onCancel: () => void
  onSave: (changes: Partial<Draft>) => void
  onCopyLink: (url: string) => void
  onEditPage: () => void
}

/*
 * EXTRAPOLATED, as in the reference: Figma draws the value 30 and no list. A
 * closed set because the cap is a storage decision, not a preference.
 */
const ITEMS_SHOWN_OPTIONS = [10, 20, 30, 50]
const DEFAULT_ITEMS_SHOWN = 30

/* The shell's GHOST, for the back link. */
const GHOST =
  "tw:text-muted-foreground tw:hover:text-foreground tw:focus-visible:text-foreground"
const ROW = "tw:px-4 tw:py-3.5"
const LABEL = "tw:text-sm tw:font-medium tw:text-foreground"
/* The shell's CONTROL_WIDTH.full: a row runs the card's width, a control does not. */
const CONTROL = "tw:w-full tw:sm:max-w-[480px]"

function draftFrom(settings?: Partial<LinkpageSettingsValues>): Draft {
  return {
    enabled: Boolean(settings?.enabled),
    title: settings?.title ?? "",
    website: settings?.website ?? "",
    itemsShown: settings?.itemsShown || DEFAULT_ITEMS_SHOWN,
  }
}

/** Only the fields that differ, and only the ones on screen. */
function changedFields(draft: Draft, saved: Draft): Partial<Draft> {
  const changes: Partial<Draft> = {}

  if (draft.enabled !== saved.enabled) changes.enabled = draft.enabled
  if (!draft.enabled) return changes

  if (draft.title.trim() !== saved.title) changes.title = draft.title
  if (draft.website.trim() !== saved.website) changes.website = draft.website
  if (draft.itemsShown !== saved.itemsShown) {
    changes.itemsShown = draft.itemsShown
  }

  return changes
}

/** Error, else help. The error says how to fix it, so it takes the help's place. */
function FieldFooter({
  id,
  error,
  help,
}: {
  id: string
  error?: string
  help: string
}) {
  return error ? (
    <p id={`${id}-error`} className="tw:text-xs tw:text-destructive">
      {error}
    </p>
  ) : (
    <p className={HELP_TEXT}>{help}</p>
  )
}

function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("tw:animate-pulse tw:rounded-md tw:bg-accent", className)}
    />
  )
}

/* The shell's SettingsFieldsSkeleton, at the height of the "on" screen. */
function FieldsSkeleton() {
  return (
    <div
      aria-hidden
      className="tw:w-full tw:divide-y tw:divide-border tw:overflow-hidden tw:rounded-xl tw:border tw:border-border tw:bg-card"
    >
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className={cn(ROW, "tw:flex tw:flex-col tw:gap-2")}>
          <Skeleton className="tw:h-4 tw:w-32" />
          <Skeleton className={cn(CONTROL, "tw:h-11 tw:md:h-8")} />
          <Skeleton className="tw:h-3 tw:w-48" />
        </div>
      ))}
    </div>
  )
}

export default function LinkpageSettings({
  accountName,
  networkLabel,
  pathSegment,
  settings,
  loading = false,
  saving = false,
  loadError,
  onRetry,
  formError,
  fieldErrors = {},
  onBack,
  onCancel,
  onSave,
  onCopyLink,
  onEditPage,
}: LinkpageSettingsProps) {
  const saved = React.useMemo(() => draftFrom(settings), [settings])
  const [draft, setDraft] = React.useState<Draft>(saved)

  /* A new record means a load or a save landed; either way it is the truth now. */
  React.useEffect(() => setDraft(saved), [saved])

  const changes = changedFields(draft, saved)
  const dirty = Object.keys(changes).length > 0
  const url = settings?.url ?? ""

  const set = <K extends LinkpageSettingsField>(field: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [field]: value }))

  /* A saved count outside the list still has to read back as itself. */
  const itemsOptions = ITEMS_SHOWN_OPTIONS.includes(draft.itemsShown)
    ? ITEMS_SHOWN_OPTIONS
    : [...ITEMS_SHOWN_OPTIONS, draft.itemsShown].sort((a, b) => a - b)

  const pageLinkHelp = pathSegment
    ? `We assign this from your ${networkLabel} username. The /${pathSegment}/ keeps it from clashing with an Instagram page of the same name. Paste it into the website field of your profile.`
    : `We assign this from your ${networkLabel} username. Paste it into the website field of your profile.`

  return (
    <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-[968px] tw:flex-col tw:gap-6 tw:p-4 tw:font-sans tw:text-foreground tw:md:p-6">
      <header className="tw:flex tw:w-full tw:flex-col tw:gap-4">
        <div className="tw:flex tw:items-center">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn(GHOST, "tw:-ml-2.5")}
            onClick={onBack}
          >
            <ChevronLeft />
            Outputs
          </Button>
        </div>
        <div className="tw:flex tw:flex-col tw:gap-1">
          {/* Sentence case, where Figma draws "Link Page Settings": the
              handoff's rule, and the sibling screen is "Global settings". */}
          <h1 className="tw:text-2xl tw:font-semibold tw:text-foreground">
            Link page settings
          </h1>
          {accountName ? (
            <p className={cn(META_TEXT, "tw:text-sm")}>
              {networkLabel ? `${accountName} · ${networkLabel}` : accountName}
            </p>
          ) : null}
        </div>
      </header>

      {loadError ? (
        <div className="tw:flex tw:w-full tw:flex-col tw:items-start tw:gap-3">
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertDescription>{loadError}</AlertDescription>
          </Alert>
          {onRetry ? (
            <Button
              type="button"
              variant="outline"
              className={TAP_TARGET}
              onClick={onRetry}
            >
              Try again
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          {formError ? (
            <Alert variant="destructive">
              <TriangleAlert />
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          ) : null}

          <div className="tw:flex tw:w-full tw:flex-col tw:gap-3">
            <p className="tw:text-sm tw:text-muted-foreground">
              A public page that collects what we post to this account, so
              followers can reach it from your bio.
            </p>

            {loading ? (
              <FieldsSkeleton />
            ) : (
              <div className="tw:w-full tw:divide-y tw:divide-border tw:overflow-hidden tw:rounded-xl tw:border tw:border-border tw:bg-card">
                {/* A toggle is a ROW: the whole text block is its label, so
                    the row is the hit target. */}
                <div className={cn(ROW, "tw:flex tw:items-center tw:gap-4")}>
                  <label
                    htmlFor="linkpage-enabled"
                    className="tw:flex tw:min-w-0 tw:flex-1 tw:cursor-pointer tw:flex-col tw:gap-1 tw:select-none"
                  >
                    <span className={LABEL}>dlvr.it link page</span>
                    <FieldFooter
                      id="linkpage-enabled"
                      error={fieldErrors.enabled}
                      help="Publishes a page at dlvrit.me and adds each item as we post it."
                    />
                  </label>
                  <Switch
                    id="linkpage-enabled"
                    checked={draft.enabled}
                    disabled={saving}
                    aria-invalid={Boolean(fieldErrors.enabled)}
                    onCheckedChange={(checked) => set("enabled", checked)}
                  />
                </div>

                {draft.enabled ? (
                  <>
                    <div className={cn(ROW, "tw:flex tw:flex-col tw:gap-2")}>
                      <label htmlFor="linkpage-url" className={LABEL}>
                        Page link
                      </label>
                      {/* The buttons sit beside the box because they act on
                          its value; below `sm` they wrap under it. 32px rather
                          than Figma's 28, per the handoff: a button beside a
                          control takes the control's height. */}
                      <div className="tw:flex tw:w-full tw:flex-wrap tw:items-center tw:gap-2">
                        <Input
                          id="linkpage-url"
                          readOnly
                          value={url}
                          className={cn(
                            TAP_TARGET,
                            "tw:min-w-0 tw:grow tw:basis-full tw:bg-input/50 tw:text-muted-foreground tw:sm:max-w-[480px] tw:sm:basis-0"
                          )}
                          onFocus={(event) => event.currentTarget.select()}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className={TAP_TARGET}
                          disabled={!url}
                          onClick={() => onCopyLink(url)}
                        >
                          Copy link
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className={TAP_TARGET}
                          onClick={onEditPage}
                        >
                          Edit page
                        </Button>
                      </div>
                      <p className={HELP_TEXT}>{pageLinkHelp}</p>
                    </div>

                    <div className={cn(ROW, "tw:flex tw:flex-col tw:gap-2")}>
                      <label htmlFor="linkpage-title" className={LABEL}>
                        Page title
                      </label>
                      <Input
                        id="linkpage-title"
                        value={draft.title}
                        placeholder={accountName}
                        disabled={saving}
                        aria-invalid={Boolean(fieldErrors.title)}
                        aria-describedby={
                          fieldErrors.title ? "linkpage-title-error" : undefined
                        }
                        className={cn(TAP_TARGET, CONTROL)}
                        onChange={(event) => set("title", event.target.value)}
                      />
                      <FieldFooter
                        id="linkpage-title"
                        error={fieldErrors.title}
                        help="Shown at the top of the page. Defaults to your account name."
                      />
                    </div>

                    {/* Not in the Figma frames: it moved here from the page's own
                        toolbar, so the page's settings live in one place. */}
                    <div className={cn(ROW, "tw:flex tw:flex-col tw:gap-2")}>
                      {/* "Your" because the Page link's help sends people to their
                          profile's website field, and this is a different box. */}
                      <label htmlFor="linkpage-website" className={LABEL}>
                        Your website
                      </label>
                      <Input
                        id="linkpage-website"
                        type="url"
                        inputMode="url"
                        value={draft.website}
                        placeholder="https://example.com"
                        disabled={saving}
                        aria-invalid={Boolean(fieldErrors.website)}
                        aria-describedby={
                          fieldErrors.website ? "linkpage-website-error" : undefined
                        }
                        className={cn(TAP_TARGET, CONTROL)}
                        onChange={(event) => set("website", event.target.value)}
                      />
                      <FieldFooter
                        id="linkpage-website"
                        error={fieldErrors.website}
                        help="Shown as a link under your handle on the page. Leave it blank for none."
                      />
                    </div>

                    <div className={cn(ROW, "tw:flex tw:flex-col tw:gap-2")}>
                      <label htmlFor="linkpage-items" className={LABEL}>
                        Items shown on the page
                      </label>
                      <div className={CONTROL}>
                        <Select
                          value={String(draft.itemsShown)}
                          disabled={saving}
                          onValueChange={(value) =>
                            set("itemsShown", parseInt(value, 10))
                          }
                        >
                          {/* h-11! / md:h-8! as in the shell: SelectTrigger
                              sizes itself with a (0,2,0) data-attribute
                              selector that a plain height class loses to. */}
                          <SelectTrigger
                            id="linkpage-items"
                            aria-invalid={Boolean(fieldErrors.itemsShown)}
                            aria-describedby={
                              fieldErrors.itemsShown
                                ? "linkpage-items-error"
                                : undefined
                            }
                            className="tw:h-11! tw:w-full tw:md:h-8!"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          {/* Portal patch: content renders outside `.tw-root`. */}
                          <SelectContent className="tw-root">
                            {itemsOptions.map((count) => (
                              <SelectItem key={count} value={String(count)}>
                                {count}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <FieldFooter
                        id="linkpage-items"
                        error={fieldErrors.itemsShown}
                        help="Older items drop off the page as new ones are added."
                      />
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>

          {loading ? null : (
            <div className="tw:flex tw:w-full tw:flex-col-reverse tw:gap-2 tw:sm:flex-row tw:sm:items-center tw:sm:justify-end">
              <Button
                type="button"
                variant="outline"
                className={TAP_TARGET}
                disabled={saving}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className={TAP_TARGET}
                loading={saving}
                disabled={!dirty || saving}
                onClick={() => onSave(changes)}
              >
                {saving ? "Saving..." : "Save changes"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
